import React from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
  Tooltip,
  TextField,
} from '@material-ui/core';
import { Edit, Delete, Check, RemoveRedEye } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import PaginationData from 'types/models/PaginationData';
import UserRoleType from 'types/enums/UserRoleType';
import { ReceiptStatus, StockOriginType, StockReception } from 'types/models/StockReceipt';
import { confirmStockReception, cancelStockReception, getStockReceptions } from 'services/stockReceptionServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import { parseDateToFormik, parseDateTimeLumen } from 'utils/datetime';
import { formatMoneyAuto } from 'utils/formatMoney';
import { getApiErrorMessage } from 'utils/errors';
import { mapToSelectOptions } from 'utils/select';
import useSetupInitialState from 'hooks/useSetupInitialState';
import ConfirmDialog from 'components/ConfirmDialog';
import SaveError from 'components/common/SaveError';
import SelectField from 'components/SelectField';
import { sumStockUnits } from 'utils/stockModule';
import Venue from 'types/models/Venue';

const STATUS_LABEL: Record<ReceiptStatus, string> = {
  [ReceiptStatus.DRAFT]: 'status.draft',
  [ReceiptStatus.CONFIRMED]: 'status.confirmed',
  [ReceiptStatus.CANCELLED]: 'status.cancelled',
};

const STATUS_OPTIONS = [ReceiptStatus.DRAFT, ReceiptStatus.CONFIRMED, ReceiptStatus.CANCELLED];

function formatDate(date: string): string {
  return new Date(date).toISOString().slice(0, 10);
}
type DialogAction = 'DELETE' | 'CONFIRM';

interface ReceptionFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  venueId?: string;
}

export default function StockReceptionsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedReception } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockReception>>();
  const [filters, setFilters] = React.useState<ReceptionFilters | null>(null);
  const [venues, setVenues] = React.useState<Venue[] | null>(null);

  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [defaultRange] = React.useState(() => ({
    from: moment().subtract(7, 'days').toDate(),
    to: moment().add(1, 'day').endOf('day').toDate(),
  }));
  const [dialog, setDialog] = React.useState<{ reception: StockReception; action: DialogAction } | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formInitialValues = {
    dateFrom: parseDateToFormik(defaultRange.from, ''),
    dateTo: parseDateToFormik(defaultRange.to, ''),
  };

  const statusFilterOptions = STATUS_OPTIONS.map((status) => ({
    label: t(STATUS_LABEL[status]),
    value: status,
  }));

  const buildFilters = (values: {
    dateFrom: string;
    dateTo: string;
    statusFilter: string[];
    venueId?: string;
  }): ReceptionFilters => ({
    dateFrom: values.dateFrom ? parseDateTimeLumen(moment(values.dateFrom).toDate(), '') : undefined,
    dateTo: values.dateTo ? parseDateTimeLumen(moment(values.dateTo).toDate(), '') : undefined,
    status: values.statusFilter.length > 0 ? values.statusFilter.join(',') : undefined,
    venueId: values.venueId || undefined,
  });

  const formik = useFormik({
    initialValues: {
      dateFrom: formInitialValues.dateFrom,
      dateTo: formInitialValues.dateTo,
      statusFilter: [] as string[],
      venueId: '',
    },
    onSubmit: () => undefined,
  });

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    formik.setFieldValue('venueId', entity.id);
  }, [isVenueEditor, entity]);

  const loadData = React.useCallback(
    async (page?: number) => {
      if (!filters) return;
      try {
        const paginationData = await getStockReceptions(
          page,
          undefined,
          filters.dateFrom,
          filters.dateTo,
          filters.status,
          filters.venueId,
        );
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        console.log(error);
      }
    },
    [filters],
  );

  React.useEffect(() => {
    if (isVenueEditor) return;
    let mounted = true;
    const loadVenues = async () => {
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.log(error);
      }
    };
    loadVenues();
    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    setCurrentPageListNumber(1);
    if (!isVenueEditor && !formik.values.venueId) {
      setFilters(null);
      setPaginationData(undefined);
      return;
    }
    setFilters(buildFilters(formik.values));
  }, [formik.values.dateFrom, formik.values.dateTo, formik.values.statusFilter, formik.values.venueId, isVenueEditor]);

  React.useEffect(() => {
    if (filters) {
      loadData(currentPageListNumber ?? 1);
    }
  }, [currentPageListNumber, loadData, filters]);

  const getActionTooltip = (reception: StockReception, action: 'view' | 'edit' | 'cancel' | 'confirm'): string => {
    const isConfirmed = reception.status === ReceiptStatus.CONFIRMED;
    const isCancelled = reception.status === ReceiptStatus.CANCELLED;

    if (action === 'view') {
      return t('stock.receipts.tooltips.view');
    }
    if (action === 'edit') {
      if (isConfirmed) return t('stock.receipts.tooltips.edit-confirmed');
      if (isCancelled) return t('stock.receipts.tooltips.edit-cancelled');
      return t('stock.receipts.tooltips.edit');
    }
    if (action === 'cancel') {
      if (isConfirmed) return t('stock.receipts.tooltips.delete-confirmed');
      if (isCancelled) return t('stock.receipts.tooltips.delete-cancelled');
      return t('stock.receipts.tooltips.cancel');
    }
    if (isConfirmed) return t('stock.receipts.tooltips.confirm-confirmed');
    if (isCancelled) return t('stock.receipts.tooltips.confirm-cancelled');
    return t('stock.receipts.tooltips.confirm');
  };

  function renderLocation(reception: StockReception) {
    if (reception.originType === StockOriginType.SUPPLIER) {
      return (
        <>
          <Chip size="small" variant="outlined" label={t(`stock.location-label.SUPPLIER`)} /> ·{' '}
          {reception.originSupplier?.businessName}
        </>
      );
    }
    return (
      <>
        <Chip size="small" variant="outlined" label={t(`stock.location-label.PRODUCTION_CENTER`)} /> ·{' '}
        {reception.originProductionCenter?.name}
      </>
    );
  }

  const renderContent = (paginationData: StockReception[]): JSX.Element => {
    return (
      <>
        {paginationData.map((reception) => (
          <TableRow key={reception.id}>
            <TableCell>{formatDate(reception.receivedAt)}</TableCell>
            <TableCell>{renderLocation(reception)}</TableCell>
            <TableCell>{reception.observations || '—'}</TableCell>
            <TableCell>
              {reception.items.length} prod. · {sumStockUnits(reception)} u.
            </TableCell>
            <TableCell>{formatMoneyAuto(reception.totalAmount)}</TableCell>
            <TableCell>
              <Chip size="small" variant="outlined" label={t(STATUS_LABEL[reception.status])} />
            </TableCell>
            <TableCell>
              {renderViewButton(reception)}
              {renderEditButton(reception)}
              {renderDeleteButton(reception)}
              {renderConfirmButton(reception)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderViewButton = (reception: StockReception): JSX.Element => {
    return (
      <Tooltip title={getActionTooltip(reception, 'view')} arrow>
        <span>
          <IconButton
            color="inherit"
            onClick={() => {
              if (!reception) return;
              setSelectedReception(reception);
              history.push(`/private/stock/receptions/${reception.id}/view`);
            }}
          >
            <RemoveRedEye />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderEditButton = (reception: StockReception): JSX.Element => {
    return (
      <Tooltip title={getActionTooltip(reception, 'edit')} arrow>
        <span>
          <IconButton
            color="inherit"
            disabled={reception.status !== ReceiptStatus.DRAFT}
            onClick={() => {
              setSelectedReception(reception);
              history.push(`/private/stock/receptions/${reception.id}/edit`);
            }}
          >
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderDeleteButton = (reception: StockReception | null): JSX.Element => {
    return (
      <Tooltip title={reception ? getActionTooltip(reception, 'cancel') : ''} arrow>
        <span>
          <IconButton
            color="inherit"
            disabled={reception?.status === ReceiptStatus.CONFIRMED || reception?.status === ReceiptStatus.CANCELLED}
            onClick={() => {
              if (!reception) return;
              setDialog({
                reception,
                action: 'DELETE',
              });
            }}
          >
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderConfirmButton = (reception: StockReception | null): JSX.Element => {
    return (
      <Tooltip title={reception ? getActionTooltip(reception, 'confirm') : ''} arrow>
        <span>
          <IconButton
            color="inherit"
            disabled={reception?.status === ReceiptStatus.CANCELLED || reception?.status === ReceiptStatus.CONFIRMED}
            onClick={() => {
              if (!reception) return;
              setDialog({
                reception,
                action: 'CONFIRM',
              });
            }}
          >
            <Check />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const onConfirmDialog = async () => {
    if (!dialog) return;

    try {
      switch (dialog.action) {
        case 'DELETE':
          await cancelStockReception(dialog.reception.id, authData?.user?.id);
          break;

        case 'CONFIRM':
          await confirmStockReception(dialog.reception.id);
          break;
      }

      setDialog(null);
      loadData();
    } catch (e) {
      setSaveError(getApiErrorMessage(e) ?? t('errors.generic'));
      console.error(e);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={dialog !== null}
        setOpen={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        title={
          dialog
            ? dialog?.action === 'DELETE'
              ? t('stock.receipts.dialog.cancel-title')
              : t('stock.receipts.dialog.confirm-title')
            : ''
        }
        description={
          dialog?.action === 'DELETE'
            ? t('stock.receipts.dialog.cancel-description')
            : t('stock.receipts.dialog.confirm-description')
        }
        onConfirm={onConfirmDialog}
        onCancel={() => setDialog(null)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {!isVenueEditor && (
          <SelectField
            label={t('stock.receipts.filters.venue')}
            name="venueId"
            placeholder={t('stock.receipts.filters.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues ?? [], { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isSearchable
            isDisabled={false}
          />
        )}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <TextField
            margin="dense"
            fullWidth
            name="dateFrom"
            label={t('stock.receipts.filters.dateFrom')}
            value={formik.values.dateFrom}
            onChange={formik.handleChange}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            fullWidth
            name="dateTo"
            label={t('stock.receipts.filters.dateTo')}
            value={formik.values.dateTo}
            onChange={formik.handleChange}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <SelectField
          name="statusFilter"
          label={t('stock.receipts.filters.status')}
          placeholder={t('stock.forms.select')}
          value={formik.values.statusFilter}
          options={statusFilterOptions}
          isMulti
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={false}
          isSearchable={false}
          onBlur={() => undefined}
          touched={formik.touched.statusFilter}
          error={formik.errors.statusFilter}
          setFieldValue={(_name: string, val: unknown) => {
            formik.setFieldValue('statusFilter', (val as string[]) ?? []);
          }}
        />
      </div>

      {(authData?.user.role === UserRoleType.Admin || authData?.user.role === UserRoleType.VenueEditor) && (
        <Button
          className="btn-50 my-6"
          variant="contained"
          color="secondary"
          onClick={() => {
            history.push('/private/stock/receptions/create');
          }}
        >
          {t('listReceptions-page-create')}
        </Button>
      )}

      <SaveError error={saveError} />
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.receipts.table.date')}</TableCell>
              <TableCell>{t('stock.receipts.table.origin')}</TableCell>
              <TableCell>{t('observations')}</TableCell>
              <TableCell>{t('stock.receipts.table.products')}</TableCell>
              <TableCell>{t('stock.receipts.table.total')}</TableCell>
              <TableCell>{t('stock.receipts.table.status')}</TableCell>
              <TableCell>{t('stock.receipts.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.receipts.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
}
